import React, { lazy, Suspense, useEffect, useState } from 'react'

// The Tawk.to widget pulls ~19 requests and keeps the network busy long after
// load, which delays first paint for every visitor — including ad traffic that
// bounces before ever opening a chat. So we don't load it with the page.
//
// It mounts on the first sign of engagement (pointer, key, scroll, touch), or
// after the browser goes idle, whichever comes first. Either way the widget is
// there well before anyone reaches for it, and first paint no longer pays for it.
const TawkMessengerReact = lazy(() => import('@tawk.to/tawk-messenger-react'))

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel']

// Don't start the idle wait until the page has had a moment to settle.
// requestIdleCallback on its own fires almost immediately on a fast connection,
// which loads the widget right back into the critical path.
const MIN_DELAY_MS = 3500

const DeferredChat = ({ propertyId, widgetId }) => {
    const [shouldLoad, setShouldLoad] = useState(false)

    useEffect(() => {
        if (shouldLoad) return
        let idleHandle
        let timeoutHandle
        let armHandle

        const load = () => {
            setShouldLoad(true)
            cleanup()
        }

        function cleanup() {
            INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, load))
            if (idleHandle && window.cancelIdleCallback) window.cancelIdleCallback(idleHandle)
            if (timeoutHandle) clearTimeout(timeoutHandle)
            if (armHandle) clearTimeout(armHandle)
        }

        // Attach the engagement listeners after a short grace period: browsers
        // fire `scroll` during scroll restoration on load, which would otherwise
        // count as engagement and pull the widget straight back into the
        // critical path.
        armHandle = setTimeout(() => {
            INTERACTION_EVENTS.forEach((evt) =>
                window.addEventListener(evt, load, { once: true, passive: true })
            )
        }, 1000)

        // Wait out the floor first, then take the next idle slot so the widget
        // never competes with the page's own work.
        timeoutHandle = setTimeout(() => {
            if (window.requestIdleCallback) {
                idleHandle = window.requestIdleCallback(load, { timeout: 2000 })
            } else {
                load()
            }
        }, MIN_DELAY_MS)

        return cleanup
    }, [shouldLoad])

    if (!shouldLoad) return null

    return (
        <Suspense fallback={null}>
            <TawkMessengerReact propertyId={propertyId} widgetId={widgetId} />
        </Suspense>
    )
}

export default DeferredChat
